import * as Phaser from "phaser-ce"
import { log } from "../../../../common/Logging"
import { ActivityKey } from "../../../../shared/models/ActivityKey"
import { CigaretteSprite } from "../../../generic-sprites/CigaretteSprite"
import { GenericAssets, loadGenericAssets } from "../../../generic-sprites/GenericAssets"
import { Sound } from "../../../generic-sprites/lip-sync/Sound"
import { Word } from "../../../generic-sprites/lip-sync/Word"
import { ProjectorSprite } from "../../../generic-sprites/ProjectorSprite"
import { ScientistSprite } from "../../../generic-sprites/ScientistSprite"
import { ThrobberSprite } from "../../../generic-sprites/ThrobberSprite"
import { TitleSprite } from "../../../generic-sprites/TitleSprite"
import { AudioSync } from "../../../utils/AudioSync"
import { CameraZoom, ZoomInfo } from "../../../utils/CameraZoom"
import { ColorUtils } from "../../../utils/ColorUtils"
import { PhaserUtils } from "../../../utils/PhaserUtils"
import { Scale } from "../../../utils/Scale"
import { Sprites } from "../../../utils/Sprites"
import { AddictionRepo } from "../AddictionRepo"
import { loadAssets, NicotineAssets } from "./NicotineAssets"
import { NicPos } from "./NicPos"

export class NicotineState extends Phaser.State {
    private static readonly FIRST_PERSON_SCALE = 0.48
    private static readonly HEAD_ONLY_SCALE = 0.7
    private static readonly SMALL_PERSON_SCALE = 0.55

    private title: TitleSprite
    private scientist: ScientistSprite
    private projector: ProjectorSprite
    private firstPerson = new PersonGroup()
    private secondPerson = new PersonGroup()
    private smallPerson = new PersonGroup()
    private lungs: ThrobberSprite
    private windpipe: ThrobberSprite
    private buzzer: Phaser.Sound
    private happyJingle: Phaser.Sound

    private readonly words: Array<Word> = [
        new Word(181, 1187, [Sound.CON, Sound.E, Sound.MBP, Sound.CON, Sound.E, Sound.CON], "In this experiment"),
        new Word(1591, 1740, [Sound.L], "you'll"),
        new Word(1741, 1987, [Sound.FV], "find"),
        new Word(1988, 2012, [Sound.O], "out"),
        new Word(2013, 2144, [Sound.O], "how"),
        new Word(2145, 2845, [Sound.CON, Sound.O, Sound.CON], "nicotine"),
        new Word(2886, 3274, [Sound.O, Sound.CON], "works"),
        new Word(3925, 4610, [Sound.CON, Sound.O, Sound.CON, Sound.A], "Nicotine is a"),
        new Word(4684, 5014, [Sound.E, Sound.L], "chemical"),
        new Word(5055, 5360, [Sound.O, Sound.CON], "found in"),
        new Word(5393, 5838, [Sound.CON, Sound.O, Sound.MBP, Sound.O], "tobacco,"),

        new Word(52717, 53204, [Sound.CON, Sound.O, Sound.CON], "nicotine"),
        new Word(53369, 53872, [Sound.MBP, Sound.E], "permanently"),
        new Word(53937, 54292, [Sound.CON, Sound.A], "changes"),
        new Word(54350, 54473, [Sound.CON], "their"),
        new Word(54548, 54952, [Sound.A], "brains"),
        new Word(55249, 55644, [Sound.A, Sound.MBP, Sound.A, Sound.CON], "and makes it"),
        new Word(55760, 56000, [Sound.A], "hard"),
        new Word(56090, 56345, [Sound.O, Sound.MBP], "for them"),
        new Word(56411, 56692, [Sound.O, Sound.L], "to live"),
        new Word(56774, 57038, [Sound.CON, Sound.O], "without"),
        new Word(57079, 57492, [Sound.CON, Sound.O, Sound.CON], "nicotine."),
    ]

    preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setGameBackgroundColorGreen(this.game)
        this.game.world.setBounds(0, 0, Scale.width() * 1.5, Scale.height())

        this.title = new TitleSprite(this.game, NicPos.titleY(), "Nicotine and Addiction", false)

        this.scientist = new ScientistSprite(this.game, NicPos.scientistX(), NicPos.scientistY(),
            NicotineAssets.AUDIO, 0.88, this.words, undefined, ActivityKey.ADDICTION_MAIN, () => {
                AddictionRepo.instance.setNicotineAndAddictionAsComplete()
            })

        this.projector = new ProjectorSprite(this.game, NicPos.projectorX(), NicPos.projectorY(), 0.47)

        this.firstPerson.group = this.game.add.group()
        this.lungs = new ThrobberSprite(this.game, NicPos.lungsX(), NicPos.lungsY(), NicotineAssets.LUNGS, NicotineState.FIRST_PERSON_SCALE)
        this.firstPerson.group.add(this.lungs)
        this.lungs.throb()
        this.firstPerson.body = this.firstPerson.group.create(NicPos.body1X(), NicPos.body1Y(), NicotineAssets.BODY)
        this.windpipe = this.firstPerson.group.create(NicPos.windpipeX(), NicPos.windpipeY(), NicotineAssets.WINDPIPE)
        this.windpipe.alpha = 0.5
        this.firstPerson.brain = new ThrobberSprite(this.game, NicPos.brain1X(), NicPos.brain1Y(), NicotineAssets.BRAIN, NicotineState.FIRST_PERSON_SCALE)
        this.firstPerson.group.add(this.firstPerson.brain)
        this.firstPerson.brain.throb()

        this.firstPerson.group.forEach((bodyPart) => {
            bodyPart.mask = this.projector.projectorMask
            Scale.scaleSprite(bodyPart, NicotineState.FIRST_PERSON_SCALE)
        }, this)
        log.debug("Applying projector mask to bodyParts")

        Scale.scaleSprite(this.firstPerson.body, 0.5)

        this.firstPerson.cig = new CigaretteSprite(this.game, NicPos.cig1X(), NicPos.cig1Y(), 0.36, this.firstPerson.group)

        this.secondPerson.group = this.game.add.group()
        this.addPersonAndBrainAndCig(this.secondPerson, this.projector.x + this.projector.width, NicPos.body1Y2())
        this.addSwirl(this.secondPerson)
        this.smallPerson.group = this.game.add.group()
        this.addPersonAndBrainAndCig(this.smallPerson, NicPos.body3X(), NicPos.body3Y(), false)

        this.buzzer = this.game.add.sound(GenericAssets.BUZZER, 0.3)
        this.happyJingle = this.game.add.sound(NicotineAssets.HAPPY_JINGLE, 0.3)

        this.setupTimers()
    }

    setupTimers = () => {
        let undoZoom: ZoomInfo
        const oneOffs = new AudioSync(this.game, this.scientist.audio)

        oneOffs.add(5000, () => {
            undoZoom = CameraZoom.zoom(this.game, this.projector, 1000, Scale.width() * 0.005)
            this.title.destroy()
            this.firstPerson.cig.smoke()
        })

        oneOffs.add(11000, () => {
            const smokeSprite = this.game.add.sprite(NicPos.smokeX(), NicPos.smokeY(), NicotineAssets.SMOKE)
            const smokeSpriteScale = (NicotineState.FIRST_PERSON_SCALE + 0.01) * 8.3
            log.debug("smoke sprite scale: " + smokeSpriteScale)
            Scale.scaleSprite(smokeSprite, smokeSpriteScale)
            smokeSprite.alpha = 0.5
            const smoke = "smoke"
            smokeSprite.animations.add(smoke)
            smokeSprite.play(smoke, 9, false, true)
        })

        oneOffs.add(23500, () => {
            // view just brain
            this.firstPerson.group.forEach((bodyPart) => {
                Sprites.fade(bodyPart, {fadeIn: false})
            }, this)
            this.firstPerson.group = this.game.add.group()
            this.addPersonAndBrainAndCig(this.firstPerson, NicPos.body1X2(), NicPos.body1Y2())
            this.firstPerson.group.forEach((bodyPart) => {
                bodyPart.alpha = 0
                Sprites.fade(bodyPart)
            }, this)
        })

        oneOffs.add(29000, () => {
            this.addSwirl(this.firstPerson)
        })

        oneOffs.add(33000, () => {
            this.firstPerson.cig.stopSmoke()
            this.colorBrain(this.firstPerson, NicotineAssets.RED_BRAIN)
            this.buzzer.play()
            Sprites.fade(this.firstPerson.swirl, {fadeIn: false})
        })

        oneOffs.add(34000, () => {
            this.slidePersonLeft(this.secondPerson, NicPos.body2X())
            this.slidePersonLeft(this.firstPerson, NicPos.body1X3())
        })

        oneOffs.add(36000, () => {
            this.colorBrain(this.secondPerson, NicotineAssets.GREEN_BRAIN)
            this.happyJingle.play()
            Sprites.fade(this.secondPerson.swirl, {fadeIn: false})
        })

        oneOffs.add(38000, () => {
            this.firstPerson.group.forEach((bodyPart) => {
                Sprites.fade(bodyPart, {fadeIn: false})
            }, this)
        })

        oneOffs.add(40000, () => {
            this.slidePersonRight(this.smallPerson, NicPos.body3X2())
        })

        oneOffs.add(45000, () => {
            this.colorBrain(this.smallPerson, NicotineAssets.GREEN_BRAIN, true)
        })

        oneOffs.add(48000, () => {
            this.smallPerson.brain.throb(0, null, 3, 0.04)
        })

        oneOffs.add(52000, () => {
            this.colorBrain(this.smallPerson, NicotineAssets.GRAY_BRAIN, true)
            this.colorBrain(this.secondPerson, NicotineAssets.GRAY_BRAIN)
        })

        oneOffs.add(53500, () => {
            CameraZoom.performZoom(undoZoom)
        })
        oneOffs.start()
    }

    slidePersonLeft = (personGroup: PersonGroup, slideBodyTo: number) => {
        const slideAmount = personGroup.body.x - slideBodyTo
        CameraZoom.moveSprite(personGroup.body, slideBodyTo)
        CameraZoom.moveSprite(personGroup.cig, personGroup.cig.x - slideAmount)
        CameraZoom.moveThrobber(personGroup.brain, personGroup.brain.defaultX - slideAmount)
        if (personGroup.swirl) {
            CameraZoom.moveSprite(personGroup.swirl, personGroup.swirl.x - slideAmount)
        }
    }

    slidePersonRight = (personGroup: PersonGroup, slideBodyTo: number) => {
        const slideAmount = slideBodyTo - personGroup.body.x
        CameraZoom.moveSprite(personGroup.body, slideBodyTo)
        CameraZoom.moveSprite(personGroup.cig, personGroup.cig.x + slideAmount)
        CameraZoom.moveThrobber(personGroup.brain, personGroup.brain.defaultX + slideAmount)
        if (personGroup.swirl) {
            CameraZoom.moveSprite(personGroup.swirl, personGroup.swirl.x + slideAmount)
        }
    }

    addPersonAndBrainAndCig = (personGroup: PersonGroup, bodyX: number, bodyY: number, large = true) => {

        const scale = large ? NicotineState.HEAD_ONLY_SCALE : NicotineState.SMALL_PERSON_SCALE

        personGroup.body = personGroup.group.create(bodyX, bodyY, NicotineAssets.BODY)
        Scale.scaleSprite(personGroup.body, scale + 0.02)

        personGroup.brain = new ThrobberSprite(this.game,
            bodyX + (Scale.width() * (large ? 0.169 : 0.135)),
            bodyY + (Scale.width() * (large ? 0.01 : 0.007)),
            NicotineAssets.BRAIN, scale)
        personGroup.group.add(personGroup.brain)
        personGroup.brain.throb()

        personGroup.cig = new CigaretteSprite(this.game,
            bodyX + (Scale.width() * (large ? 0.12 : 0.1)),
            bodyY + (Scale.width() * (large ? 0.05 : 0.04)),
            scale * 0.7, personGroup.group)
        personGroup.cig.smoke()

        personGroup.group.forEach((bodyPart) => {
            bodyPart.mask = this.projector.projectorMask
        }, this)
        log.debug("Applying projector mask to personGroup")
    }

    addSwirl = (personGroup: PersonGroup) => {
        personGroup.swirl = this.game.add.sprite(personGroup.brain.x - (Scale.width() * 0.005),
            personGroup.brain.y - (Scale.height() * 0.005), NicotineAssets.SWIRL)
        Scale.scaleSprite(personGroup.swirl, 0.6)
        const swirlName = "swirl"
        personGroup.swirl.animations.add(swirlName)
        personGroup.swirl.animations.play(swirlName, 10, true)
        personGroup.swirl.alpha = 0
        personGroup.swirl.mask = this.projector.projectorMask
        log.debug("Applying projector mask to swirl")
        Sprites.fade(personGroup.swirl)
    }

    colorBrain = (personGroup: PersonGroup, brainAsset: string, smallPerson = false) => {
        const scale = smallPerson ? NicotineState.SMALL_PERSON_SCALE : NicotineState.HEAD_ONLY_SCALE
        const colorBrain = new ThrobberSprite(this.game, personGroup.brain.x, personGroup.brain.y, brainAsset, scale)
        personGroup.group.add(colorBrain)

        const colorStrobe = "colorStrobe"
        colorBrain.animations.add(colorStrobe)
        if (brainAsset !== NicotineAssets.GRAY_BRAIN) {
            colorBrain.throb(0, null, 10, 0.03)
            colorBrain.animations.play(colorStrobe, 20, true)
        } else {
            colorBrain.animations.play(colorStrobe, 4, false)
        }
        colorBrain.alpha = 0

        const previousBrain = personGroup.brain
        personGroup.brain = colorBrain

        Sprites.fade(colorBrain)
        Sprites.fade(previousBrain, {
            fadeIn: false, onComplete: () => {
                personGroup.group.remove(previousBrain)
                previousBrain.destroy()
            }
        })
    }

}

class PersonGroup {
    group: Phaser.Group
    body: Phaser.Sprite
    brain: ThrobberSprite
    cig: CigaretteSprite
    swirl: Phaser.Sprite
}
